import { AuthOptions, ISODateString } from "next-auth";
import CredentialsProvider from "next-auth/providers/credentials";
import { config } from "@/config/config";
import axios from "axios";
import { ROLES } from "./constants";
import {
  businessDashboard,
  cmDashboard,
  ptaDashboard,
  userRoles,
} from "@/config/routes";
import GoogleProvider from "next-auth/providers/google";

export interface CustomSession {
  user?: any;
  expires: ISODateString;
}

export async function getSession() {
  const { getServerSession } = await import("next-auth");
  const session: CustomSession | null = await getServerSession(authOptions);
  return session;
}

export const loginAndRedirect = async (
  email: string,
  password: string,
  redirect: any,
) => {
  const { signIn, getSession } = await import("next-auth/react");

  const logged: any = await signIn("credentials", {
    email,
    password,
    redirect: false,
  });

  if (logged.ok) {
    const session = await getSession();
    const role = session?.user?.role?.guard_name;

    redirectByRole(role, redirect);
  } else {
    throw new Error(logged.error);
  }
};

const postSocialLogin = async (
  provider: string,
  access_provider_token: string,
) => {
  try {
    const res: any = await axios.post(`${config.api_url}/login/callback`, {
      provider,
      access_provider_token,
    });
    const user = { ...res?.data.user, token: res.data.token };

    if (user) {
      return user;
    } else {
      return null;
    }
  } catch (error: any) {
    console.error(error.response.data);
    throw new Error(JSON.stringify(error.response.data));
  }
};

export const authOptions: AuthOptions = {
  pages: {
    signIn: "/sign-in",
    newUser: "/",
    verifyRequest: "/email-verified",
    signOut: "/",
  },
  secret: process.env.NEXTAUTH_SECRET,
  callbacks: {
    async signIn({ user, account, email, profile, credentials }) {
      if (user) {
        return true;
      }

      return false;
    },
    async jwt({ token, trigger, user, session, account }) {
      if (
        (account?.provider !== "credentials" && trigger === "signIn") ||
        trigger === "signUp"
      ) {
        if (!account?.access_token) {
          throw new Error("Social provider login failed");
        }
        user = await postSocialLogin(account?.provider, account?.access_token);
        if (!user) {
          throw new Error("Social provider login failed");
        }
      }
      if (user) {
        token.data = { ...user, provider: account?.provider };
      }
      if (trigger === "update") {
        if (token.data && session.data.token) {
          token = {
            ...token,
            data: {
              ...token.data,
              ...session.data.token,
            },
          };
        }
      }

      return token;
    },
    async session({ session, token }: { session: CustomSession; token: any }) {
      if (token) {
        session.user = token.data;
      }
      return session;
    },
  },

  providers: [
    CredentialsProvider({
      name: "credentials",
      credentials: {},
      async authorize(credentials) {
        try {
          const res: any = await axios.post(
            `${config.api_url}/login`,
            credentials,
          );
          const user = { ...res?.data.user, token: res.data.token };

          if (user) {
            return user;
          } else {
            return null;
          }
        } catch (error: any) {
          console.error(error.response.data);
          throw new Error(JSON.stringify(error.response.data));
        }
      },
    }),
    GoogleProvider({
      clientId: config.google_auth_id as string,
      clientSecret: config.google_auth_secret as string,
    }),
  ],
};

export function redirectByRole(role: string | undefined, redirect: any) {
  switch (role) {
    case ROLES.BusinessOwner:
      redirect(businessDashboard);
      break;
    case ROLES.OrganizationAdmin:
      redirect(ptaDashboard);
      break;
    case ROLES.CommunityMember:
      redirect(cmDashboard);
      break;
    case undefined:
      redirect(userRoles);
      break;
  }
}
